import React, { ReactNode, SFC } from 'react';
import styled from 'styled-components';

import { ColumnProps } from 'antd/lib/table';
import { Table } from './Table';
import { TableNumberCell } from './TableNumberCell';
import { TextBlock } from './TextBlock';
import { TotalTable } from './TotalTable';

import { ImpuestosDesgloseRecord, ImpuestosDesgloseTotalRecord } from '../../models';

interface ImpuestosDesgloseTablesProps {
    isReadonly: boolean;
    isLoading: boolean;
    ejercicio: number;
    description: string;
    desgloseEjercicioRecords: ImpuestosDesgloseRecord[];
    desgloseEjercicioAnteriorRecords: ImpuestosDesgloseRecord[];
    desgloseTotalRecords: ImpuestosDesgloseTotalRecord[];
    onUpdateEjercicioRecord: (path: string, value: number) => void;
    onUpdateEjercicioAnteriorRecord: (path: string, value: number) => void;
}

const Header = styled(Table)`
    .ant-table-placeholder {
        display: none;
    }
`;

const headerColumns: Array<ColumnProps<{}>> = [
    {
        title: 'CONCEPTO',
        key: 'concepto',
        align: 'center'
    },
    {
        title: <TextBlock text="IMPORTE\n(PESOS)" />,
        key: 'importe',
        width: 392
    }
];

const generateDesgloseColumns = (
    ejercicio: number,
    renderImporte: (path: string, value: number) => ReactNode
): Array<ColumnProps<ImpuestosDesgloseRecord>> => [
    {
        dataIndex: 'concepto'
    },
    {
        dataIndex: 'importe',
        width: 392,
        align: 'right',
        render: (_, record) => renderImporte(record.key, record.importe)
    }
];

export const ImpuestosDesgloseTables: SFC<ImpuestosDesgloseTablesProps> = ({
    isReadonly,
    isLoading,
    ejercicio,
    description,
    desgloseEjercicioRecords,
    desgloseEjercicioAnteriorRecords,
    desgloseTotalRecords,
    onUpdateEjercicioRecord,
    onUpdateEjercicioAnteriorRecord
}) => {
    const renderEjercicioTitle = () => `EJERCICIO ${ejercicio}`;

    const renderEjercicioAnteriorTitle = () => `EJERCICIO ANTERIORES A ${ejercicio}`;

    const renderImporte = (handleChange: (path: string, value: number) => void) => (
        path: string,
        value: number
    ) => (
        <TableNumberCell
            type="decimal"
            path={path}
            value={value}
            isReadonly={isReadonly}
            onChange={handleChange}
        />
    );

    const desgloseEjercicioColumns = generateDesgloseColumns(
        ejercicio,
        renderImporte(onUpdateEjercicioRecord)
    );

    const desgloseEjercicioAnteriorColumns = generateDesgloseColumns(
        ejercicio,
        renderImporte(onUpdateEjercicioAnteriorRecord)
    );

    const totalValueColumn: ColumnProps<ImpuestosDesgloseTotalRecord> = {
        dataIndex: 'importe',
        align: 'right',
        width: 392,
        render: (_, record) => (
            <TableNumberCell
                type="decimal"
                isReadonly={true}
                value={record.importe}
                maxValue={1e16}
            />
        )
    };

    return (
        <>
            <Header columns={headerColumns} />
            <TextBlock text={description} />
            <Table
                title={renderEjercicioTitle}
                showHeader={false}
                columns={desgloseEjercicioColumns}
                dataSource={desgloseEjercicioRecords}
                loading={isLoading}
            />
            <Table
                title={renderEjercicioAnteriorTitle}
                showHeader={false}
                columns={desgloseEjercicioAnteriorColumns}
                dataSource={desgloseEjercicioAnteriorRecords}
                loading={isLoading}
            />
            <TotalTable
                valueColumns={[totalValueColumn]}
                dataSource={desgloseTotalRecords}
                loading={isLoading}
            />
        </>
    );
};
